import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/lib/supabase';

type ReturnStatus = 'pending' | 'approved' | 'rejected' | 'completed';
type RefundMethod = 'cash' | 'transfer' | 'store_credit' | null;

interface ReturnItem {
  id: number;
  order_id: string;
  customer_name: string;
  customer_phone: string | null;
  product_name: string;
  product_id: number | null;
  quantity: number;
  reason: string;
  status: ReturnStatus;
  admin_notes: string | null;
  refund_amount: number | null;
  refund_method: RefundMethod;
  images: string[] | null;
  created_at: string;
  updated_at: string;
}

const STATUS_CONFIG: Record<ReturnStatus, { label: string; color: string; bg: string; border: string; icon: string }> = {
  pending:   { label: 'قيد المراجعة', color: 'text-amber-700',  bg: 'bg-amber-50',   border: 'border-amber-200',  icon: 'ri-time-line' },
  approved:  { label: 'مقبول',        color: 'text-emerald-700', bg: 'bg-emerald-50', border: 'border-emerald-200', icon: 'ri-checkbox-circle-line' },
  rejected:  { label: 'مرفوض',        color: 'text-red-700',     bg: 'bg-red-50',     border: 'border-red-200',    icon: 'ri-close-circle-line' },
  completed: { label: 'مكتمل',        color: 'text-blue-700',    bg: 'bg-blue-50',    border: 'border-blue-200',   icon: 'ri-check-double-line' },
};

const REFUND_METHODS: { value: RefundMethod; label: string; icon: string }[] = [
  { value: 'cash',         label: 'نقداً',          icon: 'ri-money-dollar-circle-line' },
  { value: 'transfer',     label: 'تحويل بنكي',     icon: 'ri-bank-line' },
  { value: 'store_credit', label: 'رصيد المتجر',    icon: 'ri-gift-line' },
];

interface AddReturnModalProps {
  onClose: () => void;
  onSaved: () => void;
  onToast: (msg: string, type?: 'success' | 'error') => void;
}

function AddReturnModal({ onClose, onSaved, onToast }: AddReturnModalProps) {
  const [form, setForm] = useState({
    order_id: '',
    customer_name: '',
    customer_phone: '',
    product_name: '',
    quantity: 1,
    reason: '',
    refund_amount: '',
    refund_method: '' as RefundMethod | '',
    admin_notes: '',
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.order_id.trim() || !form.customer_name.trim() || !form.product_name.trim() || !form.reason.trim()) {
      onToast('يرجى ملء جميع الحقول المطلوبة', 'error');
      return;
    }
    setSaving(true);
    const { error } = await supabase.from('returns').insert({
      order_id: form.order_id.trim(),
      customer_name: form.customer_name.trim(),
      customer_phone: form.customer_phone.trim() || null,
      product_name: form.product_name.trim(),
      quantity: form.quantity,
      reason: form.reason.trim(),
      refund_amount: form.refund_amount ? Number(form.refund_amount) : null,
      refund_method: form.refund_method || null,
      admin_notes: form.admin_notes.trim() || null,
      status: 'pending',
    });
    setSaving(false);
    if (error) { onToast('حدث خطأ أثناء الحفظ', 'error'); return; }
    onToast('تم إضافة المرتجع بنجاح ✓');
    onSaved();
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40" dir="rtl">
      <div className="bg-white rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <h3 className="font-bold text-[#1a1a1a] flex items-center gap-2">
            <i className="ri-arrow-go-back-line text-base"></i>
            إضافة مرتجع جديد
          </h3>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 text-gray-400 cursor-pointer">
            <i className="ri-close-line"></i>
          </button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 flex flex-col gap-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-bold text-gray-600 mb-1.5">رقم الطلب <span className="text-red-500">*</span></label>
              <input
                type="text"
                value={form.order_id}
                onChange={e => setForm(f => ({ ...f, order_id: e.target.value }))}
                placeholder="مثال: ORD-12345"
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-[#1a1a1a] transition-colors"
              />
            </div>
            <div>
              <label className="block text-xs font-bold text-gray-600 mb-1.5">اسم العميل <span className="text-red-500">*</span></label>
              <input
                type="text"
                value={form.customer_name}
                onChange={e => setForm(f => ({ ...f, customer_name: e.target.value }))}
                placeholder="اسم العميل"
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-[#1a1a1a] transition-colors"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-bold text-gray-600 mb-1.5">رقم الجوال</label>
              <input
                type="text"
                value={form.customer_phone}
                onChange={e => setForm(f => ({ ...f, customer_phone: e.target.value }))}
                placeholder="05xxxxxxxx"
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-[#1a1a1a] transition-colors"
              />
            </div>
            <div>
              <label className="block text-xs font-bold text-gray-600 mb-1.5">الكمية</label>
              <input
                type="number"
                min={1}
                value={form.quantity}
                onChange={e => setForm(f => ({ ...f, quantity: Number(e.target.value) }))}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-[#1a1a1a] transition-colors"
              />
            </div>
          </div>

          <div>
            <label className="block text-xs font-bold text-gray-600 mb-1.5">اسم المنتج <span className="text-red-500">*</span></label>
            <input
              type="text"
              value={form.product_name}
              onChange={e => setForm(f => ({ ...f, product_name: e.target.value }))}
              placeholder="اسم المنتج المُرتجع"
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-[#1a1a1a] transition-colors"
            />
          </div>

          <div>
            <label className="block text-xs font-bold text-gray-600 mb-1.5">سبب الإرجاع <span className="text-red-500">*</span></label>
            <textarea
              value={form.reason}
              onChange={e => setForm(f => ({ ...f, reason: e.target.value }))}
              placeholder="اذكر سبب الإرجاع بالتفصيل..."
              rows={3}
              maxLength={500}
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-[#1a1a1a] transition-colors resize-none"
            />
            <p className="text-[10px] text-gray-400 mt-1 text-left">{form.reason.length}/500</p>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-bold text-gray-600 mb-1.5">مبلغ الاسترداد (ر.س)</label>
              <input
                type="number"
                min={0}
                step={0.01}
                value={form.refund_amount}
                onChange={e => setForm(f => ({ ...f, refund_amount: e.target.value }))}
                placeholder="0.00"
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-[#1a1a1a] transition-colors"
              />
            </div>
            <div>
              <label className="block text-xs font-bold text-gray-600 mb-1.5">طريقة الاسترداد</label>
              <select
                value={form.refund_method ?? ''}
                onChange={e => setForm(f => ({ ...f, refund_method: e.target.value as RefundMethod | '' }))}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-[#1a1a1a] transition-colors bg-white cursor-pointer"
              >
                <option value="">اختر الطريقة</option>
                {REFUND_METHODS.map(m => (
                  <option key={m.value} value={m.value ?? ''}>{m.label}</option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label className="block text-xs font-bold text-gray-600 mb-1.5">ملاحظات الأدمن</label>
            <textarea
              value={form.admin_notes}
              onChange={e => setForm(f => ({ ...f, admin_notes: e.target.value }))}
              placeholder="ملاحظات داخلية..."
              rows={2}
              maxLength={500}
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-[#1a1a1a] transition-colors resize-none"
            />
          </div>

          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 border border-gray-200 rounded-lg py-2.5 text-sm font-medium text-gray-600 hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap"
            >
              إلغاء
            </button>
            <button
              type="submit"
              disabled={saving}
              className="flex-1 bg-[#1a1a1a] text-white rounded-lg py-2.5 text-sm font-medium hover:bg-[#333] transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {saving ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-save-line"></i>}
              حفظ المرتجع
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

interface ReturnDetailModalProps {
  item: ReturnItem;
  onClose: () => void;
  onSaved: () => void;
  onToast: (msg: string, type?: 'success' | 'error') => void;
}

function ReturnDetailModal({ item, onClose, onSaved, onToast }: ReturnDetailModalProps) {
  const [status, setStatus] = useState<ReturnStatus>(item.status);
  const [adminNotes, setAdminNotes] = useState(item.admin_notes ?? '');
  const [refundAmount, setRefundAmount] = useState(item.refund_amount?.toString() ?? '');
  const [refundMethod, setRefundMethod] = useState<RefundMethod | ''>(item.refund_method ?? '');
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    const { error } = await supabase.from('returns').update({
      status,
      admin_notes: adminNotes.trim() || null,
      refund_amount: refundAmount ? Number(refundAmount) : null,
      refund_method: refundMethod || null,
      updated_at: new Date().toISOString(),
    }).eq('id', item.id);
    setSaving(false);
    if (error) { onToast('حدث خطأ أثناء الحفظ', 'error'); return; }
    onToast('تم تحديث المرتجع بنجاح ✓');
    onSaved();
    onClose();
  };

  const cfg = STATUS_CONFIG[status];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40" dir="rtl">
      <div className="bg-white rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <div>
            <h3 className="font-bold text-[#1a1a1a] flex items-center gap-2">
              <i className="ri-arrow-go-back-line text-base"></i>
              تفاصيل المرتجع #{item.id}
            </h3>
            <p className="text-xs text-gray-400 mt-0.5">طلب #{item.order_id}</p>
          </div>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 text-gray-400 cursor-pointer">
            <i className="ri-close-line"></i>
          </button>
        </div>

        <div className="p-6 flex flex-col gap-5">
          {/* Customer Info */}
          <div className="bg-gray-50 rounded-xl p-4 flex flex-col gap-2">
            <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-1">معلومات العميل</p>
            <div className="flex items-center gap-2">
              <i className="ri-user-line text-gray-400 text-sm"></i>
              <span className="text-sm font-semibold text-[#1a1a1a]">{item.customer_name}</span>
            </div>
            {item.customer_phone && (
              <div className="flex items-center gap-2">
                <i className="ri-phone-line text-gray-400 text-sm"></i>
                <span className="text-sm text-gray-600">{item.customer_phone}</span>
              </div>
            )}
            <div className="flex items-center gap-2">
              <i className="ri-shopping-bag-3-line text-gray-400 text-sm"></i>
              <span className="text-sm text-gray-600">{item.product_name} × {item.quantity}</span>
            </div>
          </div>

          {/* Reason */}
          <div>
            <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">سبب الإرجاع</p>
            <div className="bg-amber-50 border border-amber-100 rounded-xl p-4">
              <p className="text-sm text-amber-800 leading-relaxed">{item.reason}</p>
            </div>
          </div>

          {/* Status */}
          <div>
            <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">الحالة</p>
            <div className="grid grid-cols-2 gap-2">
              {(Object.keys(STATUS_CONFIG) as ReturnStatus[]).map(s => {
                const c = STATUS_CONFIG[s];
                const isActive = status === s;
                return (
                  <button
                    key={s}
                    onClick={() => setStatus(s)}
                    className={`flex items-center gap-2 px-3 py-2.5 rounded-xl border text-sm font-semibold transition-all cursor-pointer ${
                      isActive
                        ? `${c.bg} ${c.border} ${c.color} ring-2 ring-offset-1 ring-current`
                        : 'bg-white border-gray-200 text-gray-500 hover:bg-gray-50'
                    }`}
                  >
                    <i className={`${c.icon} text-base`}></i>
                    {c.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Refund */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-bold text-gray-600 mb-1.5">مبلغ الاسترداد (ر.س)</label>
              <input
                type="number"
                min={0}
                step={0.01}
                value={refundAmount}
                onChange={e => setRefundAmount(e.target.value)}
                placeholder="0.00"
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-[#1a1a1a] transition-colors"
              />
            </div>
            <div>
              <label className="block text-xs font-bold text-gray-600 mb-1.5">طريقة الاسترداد</label>
              <select
                value={refundMethod ?? ''}
                onChange={e => setRefundMethod(e.target.value as RefundMethod | '')}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-[#1a1a1a] transition-colors bg-white cursor-pointer"
              >
                <option value="">اختر الطريقة</option>
                {REFUND_METHODS.map(m => (
                  <option key={m.value} value={m.value ?? ''}>{m.label}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Admin Notes */}
          <div>
            <label className="block text-xs font-bold text-gray-600 mb-1.5">ملاحظات الأدمن</label>
            <textarea
              value={adminNotes}
              onChange={e => setAdminNotes(e.target.value)}
              placeholder="أضف ملاحظاتك هنا..."
              rows={3}
              maxLength={500}
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-[#1a1a1a] transition-colors resize-none"
            />
            <p className="text-[10px] text-gray-400 mt-1 text-left">{adminNotes.length}/500</p>
          </div>

          <div className="flex gap-3 pt-1">
            <button
              onClick={onClose}
              className="flex-1 border border-gray-200 rounded-lg py-2.5 text-sm font-medium text-gray-600 hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap"
            >
              إلغاء
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className="flex-1 bg-[#1a1a1a] text-white rounded-lg py-2.5 text-sm font-medium hover:bg-[#333] transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {saving ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-save-line"></i>}
              حفظ التغييرات
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

interface ReturnsManagerProps {
  onToast: (msg: string, type?: 'success' | 'error') => void;
}

export default function ReturnsManager({ onToast }: ReturnsManagerProps) {
  const [returns, setReturns] = useState<ReturnItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState<ReturnStatus | 'all'>('all');
  const [search, setSearch] = useState('');
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [detailItem, setDetailItem] = useState<ReturnItem | null>(null);
  const [deleteId, setDeleteId] = useState<number | null>(null);
  const [deleting, setDeleting] = useState(false);

  const fetchReturns = useCallback(async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from('returns')
      .select('*')
      .order('created_at', { ascending: false });
    setLoading(false);
    if (error) { onToast('حدث خطأ أثناء تحميل المرتجعات', 'error'); return; }
    setReturns(data ?? []);
  }, [onToast]);

  useEffect(() => { fetchReturns(); }, [fetchReturns]);

  const handleDelete = async () => {
    if (!deleteId) return;
    setDeleting(true);
    const { error } = await supabase.from('returns').delete().eq('id', deleteId);
    setDeleting(false);
    if (error) { onToast('حدث خطأ أثناء الحذف', 'error'); return; }
    onToast('تم حذف المرتجع');
    setDeleteId(null);
    fetchReturns();
  };

  const filtered = returns.filter(r => {
    const matchStatus = filterStatus === 'all' || r.status === filterStatus;
    const matchSearch = !search || r.customer_name.includes(search) || r.order_id.includes(search) || r.product_name.includes(search);
    return matchStatus && matchSearch;
  });

  const counts = {
    all: returns.length,
    pending: returns.filter(r => r.status === 'pending').length,
    approved: returns.filter(r => r.status === 'approved').length,
    rejected: returns.filter(r => r.status === 'rejected').length,
    completed: returns.filter(r => r.status === 'completed').length,
  };

  const totalRefund = returns
    .filter(r => r.status === 'approved' || r.status === 'completed')
    .reduce((sum, r) => sum + (r.refund_amount ?? 0), 0);

  return (
    <div className="flex flex-col gap-5">
      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          { label: 'إجمالي المرتجعات', value: counts.all, icon: 'ri-arrow-go-back-line', color: 'text-[#1a1a1a]', bg: 'bg-gray-50' },
          { label: 'قيد المراجعة',     value: counts.pending,  icon: 'ri-time-line',           color: 'text-amber-600',   bg: 'bg-amber-50' },
          { label: 'مقبولة',           value: counts.approved, icon: 'ri-checkbox-circle-line', color: 'text-emerald-600', bg: 'bg-emerald-50' },
          { label: 'إجمالي المسترد',   value: `${totalRefund.toLocaleString('ar-SA')} ر.س`, icon: 'ri-refund-2-line', color: 'text-[#1a1a1a]', bg: 'bg-gray-50' },
        ].map(stat => (
          <div key={stat.label} className="bg-white rounded-xl p-4 border border-gray-100">
            <div className={`w-8 h-8 flex items-center justify-center ${stat.bg} rounded-lg mb-3`}>
              <i className={`${stat.icon} ${stat.color} text-sm`}></i>
            </div>
            <p className={`text-lg font-bold ${stat.color}`}>{stat.value}</p>
            <p className="text-xs text-gray-400 mt-0.5">{stat.label}</p>
          </div>
        ))}
      </div>

      {/* Toolbar */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        <div className="flex items-center gap-2 flex-wrap">
          {(['all', 'pending', 'approved', 'rejected', 'completed'] as const).map(s => {
            const cfg = s === 'all' ? null : STATUS_CONFIG[s];
            const isActive = filterStatus === s;
            return (
              <button
                key={s}
                onClick={() => setFilterStatus(s)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold border transition-all cursor-pointer whitespace-nowrap ${
                  isActive
                    ? s === 'all'
                      ? 'bg-[#1a1a1a] text-white border-[#1a1a1a]'
                      : `${cfg!.bg} ${cfg!.color} ${cfg!.border}`
                    : 'bg-white text-gray-500 border-gray-200 hover:bg-gray-50'
                }`}
              >
                {cfg && <i className={`${cfg.icon} text-xs`}></i>}
                {s === 'all' ? 'الكل' : cfg!.label}
                <span className={`text-[10px] font-black px-1.5 py-0.5 rounded-full min-w-[18px] text-center ${
                  isActive
                    ? s === 'all' ? 'bg-white/20 text-white' : 'bg-white/60'
                    : 'bg-gray-100 text-gray-500'
                }`}>
                  {counts[s]}
                </span>
              </button>
            );
          })}
        </div>
        <div className="flex items-center gap-2 w-full sm:w-auto">
          <div className="relative flex-1 sm:w-52">
            <i className="ri-search-line absolute top-1/2 -translate-y-1/2 right-3 text-gray-300 text-sm"></i>
            <input
              type="text"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="بحث..."
              className="w-full border border-gray-200 rounded-lg pr-8 pl-3 py-2 text-sm outline-none focus:border-[#1a1a1a] transition-colors"
            />
          </div>
          <button
            onClick={() => setAddModalOpen(true)}
            className="bg-[#1a1a1a] text-white rounded-lg px-4 py-2 text-sm font-medium hover:bg-[#333] transition-colors whitespace-nowrap flex items-center gap-1.5 cursor-pointer"
          >
            <i className="ri-add-line"></i>
            مرتجع جديد
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
        {loading ? (
          <div className="flex flex-col items-center justify-center py-16 gap-3">
            <div className="w-10 h-10 border-2 border-gray-200 border-t-[#1a1a1a] rounded-full animate-spin"></div>
            <p className="text-sm text-gray-400">جاري تحميل المرتجعات...</p>
          </div>
        ) : filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 gap-3">
            <div className="w-14 h-14 flex items-center justify-center bg-gray-50 rounded-2xl">
              <i className="ri-arrow-go-back-line text-gray-300 text-2xl"></i>
            </div>
            <p className="text-sm text-gray-400 font-medium">لا توجد مرتجعات</p>
            <button
              onClick={() => setAddModalOpen(true)}
              className="text-sm text-[#1a1a1a] font-bold hover:underline cursor-pointer flex items-center gap-1"
            >
              <i className="ri-add-line"></i>
              إضافة أول مرتجع
            </button>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-100 bg-gray-50/50">
                  <th className="text-right px-5 py-3 text-xs font-bold text-gray-500 whitespace-nowrap">#</th>
                  <th className="text-right px-5 py-3 text-xs font-bold text-gray-500 whitespace-nowrap">رقم الطلب</th>
                  <th className="text-right px-5 py-3 text-xs font-bold text-gray-500 whitespace-nowrap">العميل</th>
                  <th className="text-right px-5 py-3 text-xs font-bold text-gray-500 whitespace-nowrap">المنتج</th>
                  <th className="text-right px-5 py-3 text-xs font-bold text-gray-500 whitespace-nowrap">الحالة</th>
                  <th className="text-right px-5 py-3 text-xs font-bold text-gray-500 whitespace-nowrap">المسترد</th>
                  <th className="text-right px-5 py-3 text-xs font-bold text-gray-500 whitespace-nowrap">التاريخ</th>
                  <th className="text-right px-5 py-3 text-xs font-bold text-gray-500 whitespace-nowrap">إجراءات</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((r, idx) => {
                  const cfg = STATUS_CONFIG[r.status];
                  const date = new Date(r.created_at).toLocaleDateString('ar-SA', { year: 'numeric', month: 'short', day: 'numeric' });
                  return (
                    <tr key={r.id} className="border-b border-gray-50 hover:bg-gray-50/50 transition-colors">
                      <td className="px-5 py-3.5 text-gray-400 text-xs">{idx + 1}</td>
                      <td className="px-5 py-3.5">
                        <span className="font-bold text-[#1a1a1a] text-xs bg-gray-100 px-2 py-1 rounded-lg">
                          #{r.order_id}
                        </span>
                      </td>
                      <td className="px-5 py-3.5">
                        <p className="font-semibold text-[#1a1a1a] text-sm">{r.customer_name}</p>
                        {r.customer_phone && <p className="text-xs text-gray-400">{r.customer_phone}</p>}
                      </td>
                      <td className="px-5 py-3.5">
                        <p className="text-sm text-gray-700 max-w-[160px] truncate">{r.product_name}</p>
                        <p className="text-xs text-gray-400">الكمية: {r.quantity}</p>
                      </td>
                      <td className="px-5 py-3.5">
                        <span className={`inline-flex items-center gap-1.5 text-xs font-bold px-2.5 py-1 rounded-full border ${cfg.bg} ${cfg.color} ${cfg.border}`}>
                          <i className={`${cfg.icon} text-xs`}></i>
                          {cfg.label}
                        </span>
                      </td>
                      <td className="px-5 py-3.5">
                        {r.refund_amount ? (
                          <div>
                            <p className="font-bold text-[#1a1a1a] text-sm">{r.refund_amount.toLocaleString('ar-SA')} ر.س</p>
                            {r.refund_method && (
                              <p className="text-xs text-gray-400">
                                {REFUND_METHODS.find(m => m.value === r.refund_method)?.label}
                              </p>
                            )}
                          </div>
                        ) : (
                          <span className="text-gray-300 text-xs">—</span>
                        )}
                      </td>
                      <td className="px-5 py-3.5 text-xs text-gray-400 whitespace-nowrap">{date}</td>
                      <td className="px-5 py-3.5">
                        <div className="flex items-center gap-1">
                          <button
                            onClick={() => setDetailItem(r)}
                            className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 text-gray-400 hover:text-[#1a1a1a] transition-colors cursor-pointer"
                            title="تفاصيل وتعديل"
                          >
                            <i className="ri-edit-line text-sm"></i>
                          </button>
                          <button
                            onClick={() => setDeleteId(r.id)}
                            className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-red-50 text-gray-400 hover:text-red-500 transition-colors cursor-pointer"
                            title="حذف"
                          >
                            <i className="ri-delete-bin-line text-sm"></i>
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        {!loading && filtered.length > 0 && (
          <div className="px-5 py-3 border-t border-gray-50 flex items-center justify-between">
            <p className="text-xs text-gray-400">{filtered.length} مرتجع</p>
            <div className="flex items-center gap-1.5 text-xs text-green-600">
              <i className="ri-cloud-line text-sm"></i>
              <span>محفوظ في قاعدة البيانات</span>
            </div>
          </div>
        )}
      </div>

      {/* Add Modal */}
      {addModalOpen && (
        <AddReturnModal
          onClose={() => setAddModalOpen(false)}
          onSaved={fetchReturns}
          onToast={onToast}
        />
      )}

      {/* Detail Modal */}
      {detailItem && (
        <ReturnDetailModal
          item={detailItem}
          onClose={() => setDetailItem(null)}
          onSaved={fetchReturns}
          onToast={onToast}
        />
      )}

      {/* Delete Confirm */}
      {deleteId !== null && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40" dir="rtl">
          <div className="bg-white rounded-2xl p-6 w-full max-w-sm">
            <div className="w-12 h-12 flex items-center justify-center bg-red-50 rounded-xl mb-4 mx-auto">
              <i className="ri-delete-bin-line text-red-500 text-xl"></i>
            </div>
            <h3 className="font-bold text-[#1a1a1a] text-center mb-2">حذف المرتجع</h3>
            <p className="text-sm text-gray-500 text-center mb-6">هل أنت متأكد من حذف هذا المرتجع؟ لا يمكن التراجع.</p>
            <div className="flex gap-3">
              <button
                onClick={() => setDeleteId(null)}
                disabled={deleting}
                className="flex-1 border border-gray-200 rounded-lg py-2.5 text-sm font-medium text-gray-600 hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50"
              >
                إلغاء
              </button>
              <button
                onClick={handleDelete}
                disabled={deleting}
                className="flex-1 bg-red-500 text-white rounded-lg py-2.5 text-sm font-medium hover:bg-red-600 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {deleting ? <i className="ri-loader-4-line animate-spin"></i> : null}
                حذف
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
